import { router, usePage } from '@inertiajs/vue3'
import HinokuniError from '@/js/errors/HinokuniError.js'

// eslint-disable-next-line no-undef
const sentryEnabled = VITE_SENTRY_ENABLED?.toLowerCase() === 'true'

function errorMiddleware(error) {
  const page = usePage()

  if (error instanceof HinokuniError) {
    switch (error.status) {
      case 401:
        router.visit(`/signin?next=${page.url}`)
        return null
    }
  }
  return error
}

function getEnvironment() {
  switch (location.hostname) {
    case 'app.picsellia.com':
      return 'production'
    case 'staging.picsellia.com':
      return 'staging'
    case 'trial.picsellia.com':
      return 'trial'
    default:
      return 'development'
  }
}

export default async function useErrorMiddleware(enabled, app) {
  if (enabled && sentryEnabled) {
    const sentry = await import('@sentry/vue')
    const BrowserTracingIntegration = sentry.browserTracingIntegration

    sentry.init({
      app,
      dsn: 'https://a1d79dc1bf934e9695c4d519d572316b@o1044018.ingest.sentry.io/4505466000113664',
      integrations: [
        new BrowserTracingIntegration({
          instrumentNavigation: false,
          instrumentPageLoad: false,
        }),
      ],
      tracesSampleRate: 0.01,
      environment: getEnvironment(),
      enableLogs: true,
      tracePropagationTargets: [
        /^https:\/\/staging.picsellia\.com\//,
        /^https:\/\/trial.picsellia\.com\//,
        /^https:\/\/app.picsellia\.com\//,
      ],
      beforeSend(event, hint) {
        if (!hint?.originalException) {
          return event
        }
        const error = errorMiddleware(hint.originalException)
        return error ? event : null
      },
    })

    let pageLoadSpan
    router.on('navigate', (event) => {
      if (pageLoadSpan) {
        pageLoadSpan.updateName(event.detail.page.component)
        pageLoadSpan.setAttribute(
          sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE,
          'route',
        )
        pageLoadSpan = undefined
      }
      else {
        sentry.startBrowserTracingNavigationSpan({
          op: 'navigation',
          name: event.detail.page.component,
          attributes: {
            [sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE]: 'route',
          },
        })
      }
    })

    app.provide('sentry', {
      setUser: sentry.setUser,
      setContext: sentry.setContext,
      setTag: sentry.setTag,
      captureException: sentry.captureException,
    })

    app.config.warnHandler = (msg, instance, trace) => {
      sentry.captureMessage(
        msg,
        {
          level: 'warning',
          extra: {
            trace,
            component: instance?.$options.name || instance?.$options.__file || 'Unknown',
          },
          tags: {
            source: 'vue',
          },
        },
      )
    }
  }
  else {
    app.provide('sentry', {
      setUser: () => {},
      setContext: () => {},
      setTag: () => {},
      captureException: () => {},
    })

    app.config.errorHandler = (_error) => {
      const error = errorMiddleware(_error)
      if (error) {
        console.error(error)
      }
    }
  }
}
